import { AssetClass } from '@/entities/assets'
import { AssetPriceSource } from '@/entities/assets/components/utils/enums'

import { TRANSACTION_TYPE } from '@/entities/transactions/utils/const'

import { useAssetsBunchStore } from '@/store/assets/bunch'

import { MORTGAGE_ACCOUNT_MODE_FIELD } from '../utils/const'
import { WizardAccountMode, WizardValues } from '../utils/types'
import {
  createHolding,
  createMarks,
  ensureOwnerAccount,
  ensureTagIds,
  postTransaction,
} from '../utils/create'

/** As RealEstate files it — the two wizards create the same kind of thing. */
const PROPERTY_TYPE = 'property'

/** As the Loan wizard fixes it: a mortgage is a loan. */
export const MORTGAGE_TYPE = 'loan'

/**
 * The mortgage half of the run — either a new liability asset, or a
 * transaction against one the repository already has, per the step's own
 * existing/new choice (see MortgageStep.vue). Either way the amount posted is
 * the principal: a fresh mortgage's own opening balance, or what is being
 * added against an existing one.
 */
const createMortgage = async (values: WizardValues, ownerAccountId: string): Promise<AssetClass> => {
  if (values[MORTGAGE_ACCOUNT_MODE_FIELD] === WizardAccountMode.EXISTING) {
    const mortgage = useAssetsBunchStore().getElementById(values.accountId)
    if (!mortgage) throw Error('The picked mortgage no longer exists in this repository')

    await postTransaction({
      accountId: ownerAccountId,
      assetId: mortgage.id,
      transactionType: TRANSACTION_TYPE.ADD_LIABILITY,
      currency: values.currency,
      amount: values.principal,
      date: values.date,
      description: values.accountDescription,
    })

    return mortgage
  }

  return createHolding({
    accountId: ownerAccountId,
    transactionType: TRANSACTION_TYPE.ADD_LIABILITY,
    name: values.accountName,
    type: MORTGAGE_TYPE,
    currency: values.currency,
    amount: values.principal,
    // The borrowing is dated with the purchase: the mortgage step collects no
    // date of its own, and a financed purchase completes on one day.
    date: values.date,
    // The mortgage's own description — the liability transaction's, not the
    // new asset's; see utils/create.ts's `createHolding`/`postTransaction`.
    transactionDescription: values.accountDescription,
  })
}

/**
 * Two linked records: the property, and the borrowing that paid for it.
 *
 * Ordered property first. It is the record the user came to create — the
 * mortgage is the detail the assistant noticed — so if the run fails partway,
 * what landed is the half they asked for rather than a loan with nothing
 * against it.
 *
 * The mortgage hangs off the owner's own account, the same one the property
 * does — `add liability` puts the debt where the person who owes it holds
 * their other records, not in an account named for the lender. The lender
 * only names the debt: its asset name, not its type — the mortgage step asks
 * no account type of its own, so a newly created mortgage is filed the same
 * fixed way the standalone Loan wizard files one.
 *
 * The tags are the asset's — the property's. A mortgage is not "Real estate ·
 * Investment property", and applying the asset's tags to it would put the
 * liability into every analytics cut the property belongs to.
 */
export const create = async (values: WizardValues): Promise<AssetClass[]> => {
  const tagIds = await ensureTagIds(values.tags)
  const accountId = await ensureOwnerAccount(values)

  const property = await createHolding({
    accountId,
    transactionType: TRANSACTION_TYPE.ADD_POSITION,
    name: values.assetName,
    type: values.assetType || PROPERTY_TYPE,
    currency: values.currency,
    description: values.description,
    amount: values.amount,
    date: values.date,
    tagIds,
  })

  const mortgage = await createMortgage(values, accountId)

  /*
   * The property's own valuation-step marks, plus one for the mortgage: what
   * the property cost is also what the mortgage's own price is set to — the
   * debt is stated at the value of what it financed, not at its own
   * principal, which the liability transaction above already carries.
   */
  await createMarks(property.id, values.prices)
  await createMarks(mortgage.id, [{
    date: values.date,
    close: values.principal,
    source: AssetPriceSource.MANUAL,
  }])

  return [property, mortgage]
}
